import tailwind from '../../../../../../tailwind.config'
import React, {
  useState,
  Fragment,
  useRef,
  useEffect,
  Ref,
  useImperativeHandle,
  forwardRef,
  MouseEvent,
} from 'react'
import scrollTo from 'animated-scroll-to'
import { capitalize } from 'shared/utils/string'
import { CookingTime } from '@/components/product'
import { FilterableComponentOption } from '@/components/filter-option'
import {
  useSelectedPreferences,
  getProductElementsNotFittingPreferences,
  ProductFitDescription,
} from '@/services/preferences'
import { BackArrow } from '@/components/back'
import { GreenCheckmark, SlimCheckmark } from '@/components/checkmark'
import {
  getProductMainPicture,
  placeholderImage,
  archiveProduct,
  uploadPresignedFile,
  uploadMainImage,
} from '@/services/product'
import { join, keys } from 'shared/utils/array'
import { OrderButtons } from '@/components/order-buttons'
import { Plus } from '@/components/plus'
import { useAppContext } from '@/pages/_app'
import { makePage } from '@/components/page'
import { useRouter } from 'next/router'
import { PageMeta } from '@/components/page-meta'
import { useVenueContext, VenueBasedPageProps } from '@/services/venue'
import { usePrice } from '@/components/price'
import { Spinner } from '@/components/spinner'
import { ProductForm, ProductFormRef } from '@/components/product-form'
import { DeleteButton } from '@/components/delete-button'
import { filterEvents, uiEvents } from '@/utils/event'
import { useChosenProduct } from '@/services/scroll-position'
import { map, take } from 'rxjs/operators'
import { TrashIcon } from '@/components/trash-icon'
import { PencilIcon } from '@/components/pencil-icon'
import { AdminProductButton } from '@/components/admin-product-button'
import { Product, NewOrderProductModifiers, ProductModifierGroup } from 'shared/types/api'
import { isBrowser } from '@/utils/env'
import fromEntries from 'object.fromentries'
import Link from 'next/link'
import { MoreIcon } from '@/components/more-icon'
import { webAppMobileWidth } from 'shared/misc'
import { useCropper } from '@/components/cropper'
import { truly } from 'shared/utils/types'
import { getVenueStaticPaths, getVenueStaticProps } from '@/services/venue'
import { GetStaticProps } from 'next'
import dynamic from 'next/dynamic'
import { BottomButtonProps } from '@/components/bottom-button'

const VenueBottomButton = dynamic<BottomButtonProps>(
  () => {
    return import('@/components/bottom-button').then((mod) => {
      return mod.VenueBottomButton
    })
  },
  {
    ssr: false,
  },
)

const DeliveryMap = dynamic(() => {
  return import('@/components/delivery-map')
})

export const BASKET_PRODUCT_ID_QUERY_PARAM = 'bpi'
export const SCROLL_TO_MODIFIERS_QUERY_PARAM = 'mod'
export const SOURCE_PRODUCT_GROUP_NAME = 'pg'
export const CAME_FROM_QUERY_PARAM = 'from'

declare global {
  interface AppUIEventMap {
    ProductPageImageSubmitDnd: {
      type: 'ProductPageImageSubmitDnd'
      file: File
    }
  }
}

export default makePage(
  {
    meta: {},
  },
  function ProductPage() {
    const router = useRouter()
    const productGroupName = router.query.productGroupName as string
    const productName = router.query.productName as string

    const { apiClient, t } = useAppContext()
    const venueContext = useVenueContext()!
    const {
      venue,
      venueName,
      orderManager,
      admin,
      availablePreferences,
      menuRoute,
      orderTypeHasPaymentMethods,
    } = venueContext

    const basketProductIdFromUrl = isBrowser
      ? new URL(location.href).searchParams.get(BASKET_PRODUCT_ID_QUERY_PARAM)
      : undefined
    const cameFrom = isBrowser
      ? new URL(location.href).searchParams.get(CAME_FROM_QUERY_PARAM)
      : undefined
    const [basketProductId, setBasketProductId] = useState(basketProductIdFromUrl)

    const checkoutRoute = {
      path: `/venue/${venueName}/checkout`,
    }

    const backRoute = cameFrom === 'checkout' ? checkoutRoute : menuRoute

    const { basket, deliveryMapManager } = orderManager
    const [editMode, setEditMode] = useState(false)
    const getForceEditMode = () => {
      return new URL(location.href).searchParams.has('edit')
    }

    const { requestDeliveryAddressIfNeeded } = deliveryMapManager

    useEffect(() => {
      if (!admin.isAdminView) {
        return
      }

      if (!getForceEditMode()) {
        return
      }

      setEditMode(true)
    }, [admin.isAdminView])

    const selectedPreferences = useSelectedPreferences()

    const productGroup = venue.productGroups.find((pg) => {
      return pg.name === productGroupName
    })
    const product = productGroup?.products.find((p) => {
      return p.name === productName
    })

    const productFormRef = useRef<ProductFormRef>(null)
    const modifiersSectionRef = useRef<ModifiersSectionRef>(null)

    const [editingIsLoading, setEditingIsLoading] = useState(false)
    const [archivingIsLoading, setArchivingIsLoading] = useState(false)
    useChosenProduct(venueName)

    useEffect(() => {
      if (!product) {
        return
      }

      // visitor can come to this page via "virtual" product groups such as "popular"
      const sourceProductGroupName =
        new URL(location.href).searchParams.get(SOURCE_PRODUCT_GROUP_NAME) ?? productGroupName

      uiEvents.next({
        type: 'ScrollPositionChosenProduct',
        product: {
          productName: product.name,
          productGroupName: sourceProductGroupName,
        },
      })
    }, [product])

    const basketProduct = basketProductId
      ? basket.products.get(basketProductId)
      : basket.products.filter((p) => {
          if (!orderTypeHasPaymentMethods) {
            return (
              p.productGroupName === productGroupName &&
              p.productName === productName &&
              p.quantity > 0
            )
          }

          return (
            p.productGroupName === productGroupName &&
            p.productName === productName &&
            keys(p.modifiers).length === 0
          )
        })[0]

    const [chosenModifiers, setChosenModifiers] = useState<NewOrderProductModifiers>(
      basketProduct ? basketProduct.modifiers : {},
    )
    const [chosenQuantity, setChosenQuantity] = useState(1)

    const cropperId = 'productEditDnd'
    const cropper = useCropper({
      cropperId,
      aspectRatio: 1,
      input$: uiEvents.pipe(
        filterEvents(['ProductPageImageSubmitDnd']),
        map((event) => {
          return event.file
        }),
      ),
      async uploadImage(event) {
        await uploadMainImage(venueName, product!, event.file, event.cropBox, apiClient)
      },
    })

    if (!product) {
      return null
    }

    const notFittingElements = getProductElementsNotFittingPreferences(
      product,
      availablePreferences,
      selectedPreferences,
    )

    const mainImage = getProductMainPicture(product)
    const placeholderClasses = mainImage
      ? ''
      : 'w-full h-48 object-cover border border-opaqueLightest dark:bg-backOpaqueDark'

    const goBackFromEdit = ({ forceGoingToMenuPage }: { forceGoingToMenuPage: boolean }) => {
      if (getForceEditMode() || forceGoingToMenuPage) {
        return router.push(`/venue/${venueName}`)
      }

      window.scrollTo(0, 0)
      return setEditMode(false)
    }

    const controlMode = (() => {
      const hasModifiers = product.modifierGroups.length > 0

      const setQuantityInBasket = (quantity: number, _change: '+' | '-', e: MouseEvent) => {
        if (requestDeliveryAddressIfNeeded(e.target) === 'requested') {
          return
        }

        basket.products.setQuantity(
          {
            productGroupName,
            productName,
            modifiers: chosenModifiers,
          },
          quantity,
        )
      }

      // came here from /checkout
      if (hasModifiers && basketProductIdFromUrl) {
        return {
          doneButtonCaption: t('back'),
          onQuantityChange: setQuantityInBasket,
          orderedQuantity: basketProduct?.quantity || 0,
          disallowZero: false,
          onBack: () => {
            // noop
          },
        }
      }

      if (hasModifiers && orderTypeHasPaymentMethods) {
        return {
          doneButtonCaption: t('common:add'),
          onQuantityChange: setChosenQuantity,
          orderedQuantity: chosenQuantity,
          disallowZero: true,
          onBack(e: MouseEvent<HTMLAnchorElement>) {
            // Request delivery address if a customer hasn't entered the delivery address yet
            // We allow to set quantity, but don't allow to add to the basket
            if (requestDeliveryAddressIfNeeded(e.target) === 'requested') {
              e.preventDefault()
              e.stopPropagation()
              return
            }

            const isValid = modifiersSectionRef.current!.validate()
            if (!isValid) {
              e.preventDefault()
              e.stopPropagation()
              return
            }
            basket.products.setQuantity(
              {
                productGroupName,
                productName,
                modifiers: chosenModifiers,
              },
              (currentQuantity) => {
                return currentQuantity + chosenQuantity
              },
            )
          },
        }
      }

      return {
        doneButtonCaption: t('back'),
        onQuantityChange: setQuantityInBasket,
        orderedQuantity: basketProduct?.quantity ?? 0,
        disallowZero: false,
        onBack: () => {
          // noop
        },
        onChosenModifiersChange: (modifiers: NewOrderProductModifiers, isValid: boolean) => {
          if (basketProduct) {
            if (!isValid) {
              basket.products.deleteById(basketProduct.__id)
              setBasketProductId(undefined)
              return
            }

            basket.products.updateById(basketProduct.__id, {
              modifiers,
            })
            return
          }
        },
      }
    })()

    const formId = 'editProduct'

    return (
      <form
        id={formId}
        onSubmit={async (event) => {
          event.preventDefault()
          if (editingIsLoading) {
            return
          }
          setEditingIsLoading(true)

          const formResult = await productFormRef.current!.process()
          if (formResult.type === 'validationError') {
            setEditingIsLoading(false)
            return
          }

          const submittedPicture = await (async () => {
            const { picture } = formResult
            if (!picture) {
              return null
            }

            const baseName = `${venueName}-${product.productGroupName}-${product.name}`
            const photoUrl = await uploadPresignedFile(venueName, baseName, picture.file, apiClient)

            if (photoUrl instanceof Error || !photoUrl) {
              return null
            }

            return {
              url: photoUrl,
              cropBox: picture.cropBox,
            }
          })()

          await apiClient.fetch(
            `/admin/venue/${venueName}/product/${productGroupName}/${productName}`,
            {
              method: 'POST',
              body: JSON.stringify({
                ...formResult.body,
                mainPhoto: submittedPicture ?? undefined,
              }),
            },
          )

          const newProductGroupName =
            formResult.body.productGroupName ?? formResult.body.productGroup!.name
          uiEvents.next({
            type: 'ScrollPositionChosenProduct',
            product: {
              productName: product.name,
              productGroupName: newProductGroupName,
            },
          })

          const productGroupHasChanged = product.productGroupName !== newProductGroupName
          venueContext.refreshVenue()
          venueContext.venueUpdated$.pipe(take(1)).subscribe(() => {
            setEditingIsLoading(false)
            goBackFromEdit({
              forceGoingToMenuPage: productGroupHasChanged,
            })
          })
        }}
      >
        <div>
          <PageMeta
            subTitle={product.title}
            imageUrl={mainImage?.url}
            description={product.description}
          />

          <div className="relative flex items-start justify-center">
            {editMode ? (
              <div
                className="absolute w-full"
                onClick={() => {
                  return goBackFromEdit({ forceGoingToMenuPage: false })
                }}
              >
                <BackArrow />
              </div>
            ) : (
              <Link href={backRoute.path} className="absolute w-full">
                <BackArrow />
              </Link>
            )}
            {admin.isAdminView && product.kind !== 'bridge' && !editMode && (
              <div
                className="absolute flex justify-end w-0 h-0 cursor-pointer"
                style={{
                  top: '1.25rem',
                  left: 'calc(100% - 1rem)',
                }}
              >
                {venue.posType === 'local' && (
                  <div>
                    <Link
                      href={`/venue/${venueName}/p/${product.productGroupName}/${product.name}/modifier-groups?f=p`}
                    >
                      <AdminProductButton>
                        <MoreIcon />
                      </AdminProductButton>
                    </Link>
                  </div>
                )}

                <div className="ml-2">
                  <AdminProductButton
                    onClick={() => {
                      return setEditMode(true)
                    }}
                  >
                    <PencilIcon />
                  </AdminProductButton>
                </div>
              </div>
            )}
            {cropper.isImageUploading && (
              <>
                <div className="absolute flex items-center justify-center w-full h-full">
                  <div className="w-16">
                    <Spinner />
                  </div>
                </div>
                <div
                  className="absolute w-full h-full"
                  style={{
                    background: 'white',
                    opacity: 0.25,
                  }}
                ></div>
              </>
            )}
            <img
              onDragOver={(event) => {
                event.stopPropagation()
                event.preventDefault()
              }}
              onDrop={async (event) => {
                event.stopPropagation()
                event.preventDefault()

                if (!admin.isAdmin) {
                  return
                }

                if (cropper.isImageUploading) {
                  return
                }

                const file = event.dataTransfer?.files?.[0]
                if (!file) {
                  return
                }

                uiEvents.next({ type: 'ProductPageImageSubmitDnd', file })
              }}
              className={`${placeholderClasses}`}
              src={mainImage?.url || placeholderImage.url}
              alt={product.title}
              style={{ maxHeight: `${webAppMobileWidth}px` }}
            />
          </div>
          {!editMode ? (
            <>
              <div className="px-5 py-4">
                <TitleSection
                  product={product}
                  withCheckmark={
                    notFittingElements.productFitsPreferences && selectedPreferences.hasPreferences
                  }
                />

                <div className="flex items-center text-opaque dark:text-opaqueDark">
                  <span className="mr-2">
                    {product.volume.join('/')} {product.volumeUnit}
                  </span>
                  <CookingTime cookingTimeMin={product.cookingTimeMin} />
                </div>
                <DescriptionSection {...{ product }} />
                <ComponentsSection {...{ product, notFittingElements }} />
                <NutrientsSection {...{ product }} />
              </div>
              <ModifiersSection
                ref={modifiersSectionRef}
                {...{
                  product,
                  chosenModifiers,
                  onChosenModifiersChange(modifiers, isValid) {
                    setChosenModifiers(modifiers)
                    controlMode.onChosenModifiersChange &&
                      controlMode.onChosenModifiersChange(modifiers, isValid)
                  },
                }}
              />
            </>
          ) : (
            <div className="py-4">
              <ProductForm
                ref={productFormRef}
                productGroupName={product.productGroupName}
                sourceProduct={product}
              />
              {venue.posType === 'local' && (
                <div className="px-5 mt-2">
                  <DeleteButton
                    onClick={async () => {
                      if (archivingIsLoading) {
                        return
                      }

                      if (!window.confirm(t('productName:doYouReallyWantToArchiveTheMeal'))) {
                        return
                      }

                      setArchivingIsLoading(true)
                      await archiveProduct(apiClient, venueName, product)
                      venueContext.refreshVenue()
                      venueContext.venueUpdated$.pipe(take(1)).subscribe(() => {
                        router.push(`/venue/${venueName}`)
                      })
                    }}
                  >
                    {archivingIsLoading ? (
                      <div className="w-8">
                        <Spinner color="stroke-option" />
                      </div>
                    ) : (
                      <>
                        <TrashIcon color="stroke-option" />
                        <span className="ml-1">{t('common:toArchive')}</span>
                      </>
                    )}
                  </DeleteButton>
                  <div className="mt-1 leading-tight whitespace-pre-line text-opaque dark:text-opaqueDark">
                    {t('productName:archivedMealWillDisappearFromTheMenu')}
                  </div>
                </div>
              )}
            </div>
          )}

          <VenueBottomButton>
            {!editMode ? (
              <>
                {orderManager.orderMode.value === 'delivery' && <DeliveryMap />}
                <div className="flex justify-between">
                  <Link
                    href={backRoute.path}
                    onClick={controlMode.onBack}
                    className="flex items-center justify-center h-12 text-center rounded bg-back dark:text-primaryAccentDark dark:bg-backAccentDark"
                    style={{ width: '45%' }}
                  >
                    <span className="text-lg">{controlMode.doneButtonCaption}</span>
                  </Link>
                  {!orderTypeHasPaymentMethods ? (
                    <div
                      onClick={() => {
                        router.push(backRoute.path)
                        uiEvents.pipe(filterEvents(['RouteChanged']), take(1)).subscribe(() => {
                          if (basketProduct) {
                            return basket.products.deleteById(basketProduct.__id)
                          }

                          const isValid = modifiersSectionRef.current?.validate() ?? true
                          if (!isValid) {
                            return
                          }

                          const insertedProduct = basket.products.insert({
                            productGroupName,
                            productName,
                            quantity: product.minQuantity,
                            modifiers: chosenModifiers,
                          })

                          setBasketProductId(insertedProduct.__id)
                        })
                      }}
                      className="flex items-center justify-center h-12 text-center rounded cursor-pointer bg-back dark:bg-backAccentDark dark:text-primaryAccentDark"
                      style={{ width: '45%' }}
                    >
                      {controlMode.orderedQuantity > 0 ? (
                        <>
                          <SlimCheckmark color="stroke-accent dark:stroke-primaryAccentDark" />
                          <span className="ml-1 text-lg text-accent dark:text-primaryAccentDark">
                            {t('common:selected')}
                          </span>
                        </>
                      ) : (
                        <>
                          <Plus color="inline stroke-primary dark:stroke-primaryDark" />
                          <span className="ml-1 text-lg text-primary dark:text-primaryAccentDark">
                            {venue.features && venue.features.serviceType === 'waiter'
                              ? t('common:select')
                              : t('common:order')}
                          </span>
                        </>
                      )}
                    </div>
                  ) : (
                    <div className="w-[45%]">
                      <OrderButtons
                        counterStyle="plain"
                        product={product}
                        quantity={controlMode.orderedQuantity}
                        onQuantityChange={controlMode.onQuantityChange}
                        disallowZero={controlMode.disallowZero}
                      />
                    </div>
                  )}
                </div>
              </>
            ) : (
              <button
                className="flex items-center justify-center w-full h-12 text-lg rounded-lg bg-back dark:bg-backAccentDark dark:text-primaryAccentDark font-medium"
                type="submit"
                form={formId}
              >
                {editingIsLoading ? (
                  <div className="inline-block w-6 mx-auto">
                    <Spinner />
                  </div>
                ) : (
                  <span>{t('common:done')}</span>
                )}
              </button>
            )}
          </VenueBottomButton>
        </div>
      </form>
    )
  },
)

function TitleSection({ product, withCheckmark }: { product: Product; withCheckmark: boolean }) {
  const price = usePrice()
  const titleWords = product.title.split(' ')
  const titleFirstWords = titleWords.slice(0, titleWords.length - 1)
  const titleLastWord = titleWords.slice(titleWords.length - 1, titleWords.length)
  return (
    <div className="flex justify-between mb-px text-2xl leading-tight">
      <h3>
        {titleFirstWords.join(' ')}{' '}
        <span className="whitespace-nowrap">
          {titleLastWord}{' '}
          {withCheckmark && (
            <GreenCheckmark className="relative inline w-4 h-4" style={{ top: '-10px' }} />
          )}
        </span>
      </h3>
      {product.price > 0 && (
        <span className="flex-shrink-0 ml-2">{price.format(product.price)}</span>
      )}{' '}
    </div>
  )
}

function DescriptionSection({ product }: { product: Product }) {
  const { t } = useAppContext()
  const { availablePreferences } = useVenueContext()!

  return (
    <div className="pt-2 leading-tight break-words whitespace-pre-line text-opaque dark:text-opaqueDark">
      {product.description}
      {product.allergenNames && product.allergenNames.length > 0 && (
        <div className="mt-2">
          {t('common:contains')}{' '}
          <span
            dangerouslySetInnerHTML={{
              __html: product.allergenNames
                .map((allergenName) => {
                  return availablePreferences.allAllergens.find((allergen) => {
                    return allergenName === allergen.name
                  })
                })
                .filter(truly)
                .map((allergen) => {
                  return t(allergen.title).toLowerCase()
                })
                .join(', '),
            }}
          />
        </div>
      )}
      {product.foodRestrictionNames && product.foodRestrictionNames.length > 0 && (
        <div className="flex flex-wrap mt-2 leading-normal">
          {product.foodRestrictionNames
            .map((foodRestrictionName) => {
              return availablePreferences.allFoodRestrictions.find((foodRestriction) => {
                return foodRestrictionName === foodRestriction.name
              })
            })
            .filter(truly)
            .map((foodRestriction) => {
              return (
                <div key={foodRestriction.name} className="flex items-center flex-shrink-0 mr-3">
                  <img
                    src={foodRestriction.imageUrl}
                    className="flex-shrink-0 inline-block h-5 mr-1"
                  />
                  <span
                    className="flex-shrink-0"
                    dangerouslySetInnerHTML={{ __html: t(foodRestriction.title) }}
                  />
                </div>
              )
            })}
        </div>
      )}
    </div>
  )
}

function ComponentsSection({
  product,
  notFittingElements,
}: {
  product: Product
  notFittingElements: ProductFitDescription
}) {
  const { t } = useAppContext()

  const [seeDetailedIngredients, setSeeDetailedIngredients] = useState<boolean>(false)

  const ingredients = product.ingredients
  const rootComponents = product.components

  const notFittingIngredientNames = notFittingElements.ingredients.map((i) => {
    return i.name
  })
  const notFittingRootComponentNames = notFittingElements.rootComponents.map((c) => {
    return c.name
  })

  return (
    <div
      className="pt-2 leading-tight text-opaque dark:text-opaqueDark"
      onClick={() => {
        setSeeDetailedIngredients(!seeDetailedIngredients)
      }}
    >
      {(() => {
        if (ingredients.length === 0 && rootComponents.length === 0) {
          return
        }
        const unremovableNotFittingIngredientComponents = (ingredientName: string) => {
          return (
            notFittingElements.ingredients.find((i) => {
              return i.name === ingredientName
            })?.components || []
          )
        }

        const unremovableNotFittingRootComponents = rootComponents.filter((component) => {
          return notFittingRootComponentNames.includes(component.name)
        })

        const onlyOneIngredient = ingredients.length === 1 && rootComponents.length === 0
        const theOnlyIngredient = onlyOneIngredient ? ingredients[0] : null

        const ingredientsToShow = onlyOneIngredient ? [] : ingredients
        const rootComponentsToShow = onlyOneIngredient ? [] : rootComponents
        const ingredientComponentsToShow = onlyOneIngredient ? theOnlyIngredient!.components : []

        if (!seeDetailedIngredients) {
          return (
            <>
              {join(
                [
                  ...ingredientsToShow.map((ingredient) => {
                    const colorClass = notFittingIngredientNames.includes(ingredient.name)
                      ? 'text-negative dark:text-negativeDark'
                      : ''

                    return {
                      title: ingredient.title,
                      element: <span className={`inline ${colorClass}`}>{ingredient.title}</span>,
                    }
                  }),
                  ...ingredientComponentsToShow.map((component) => {
                    const colorClass = unremovableNotFittingIngredientComponents(
                      theOnlyIngredient!.name,
                    ).some((c) => {
                      return c.name === component.name
                    })
                      ? 'text-negative dark:text-negativeDark'
                      : ''

                    return {
                      title: component.title,
                      element: <span className={`inline ${colorClass}`}>{component.title}</span>,
                    }
                  }),
                  ...rootComponentsToShow.map((component) => {
                    const colorClass = unremovableNotFittingRootComponents.some((c) => {
                      return c.name === component.name
                    })
                      ? 'text-negative dark:text-negativeDark'
                      : ''

                    return {
                      title: component.title,
                      element: <span className={`inline ${colorClass}`}>{component.title}</span>,
                    }
                  }),
                ]
                  .sort((a, b) => {
                    return a.title < b.title ? -1 : 1
                  })
                  .map((x) => {
                    return x.element
                  }),
                <span>{' • '}</span>,
              ).map((element, idx) => {
                return <Fragment key={idx}>{element}</Fragment>
              })}{' '}
              <span className="inline px-2 py-px rounded cursor-pointer bg-backScreen dark:bg-backScreenDark">
                {t('productName:details')}
              </span>
            </>
          )
        }

        const listItems = [
          ...ingredientsToShow.map((ingredient) => {
            const colorClass = notFittingIngredientNames.includes(ingredient.name)
              ? 'text-negative dark:text-negativeDark'
              : ''
            const components = join(
              ingredient.components
                .sort((a, b) => {
                  return a.title < b.title ? -1 : 1
                })
                .map((component) => {
                  const colorClass = unremovableNotFittingIngredientComponents(
                    ingredient.name,
                  ).some((c) => {
                    return c.name === component.name
                  })
                    ? 'text-negative dark:text-negativeDark'
                    : ''
                  return (
                    <span key={component.name} className={`inline ${colorClass}`}>
                      {component.title}
                    </span>
                  )
                }),
              <>{', '}</>,
            )

            return {
              title: ingredient.title,
              element: (
                <>
                  <span className={`inline ${colorClass}`}>{capitalize(ingredient.title)}</span>{' '}
                  <span>
                    {components ? (
                      <>
                        (
                        {components.map((el, idx) => {
                          return <Fragment key={idx}>{el}</Fragment>
                        })}
                        )
                      </>
                    ) : null}
                  </span>
                </>
              ),
            }
          }),
          ...ingredientComponentsToShow.map((component) => {
            const colorClass = unremovableNotFittingIngredientComponents(
              theOnlyIngredient!.name,
            ).some((c) => {
              return c.name === component.name
            })
              ? 'text-negative dark:text-negativeDark'
              : ''

            return {
              title: component.title,
              element: (
                <span className={`inline ${colorClass}`}>{capitalize(component.title)}</span>
              ),
            }
          }),
          ...rootComponentsToShow.map((component) => {
            const colorClass = unremovableNotFittingRootComponents.some((c) => {
              return c.name === component.name
            })
              ? 'text-negative dark:text-negativeDark'
              : ''

            return {
              title: component.title,
              element: (
                <span className={`inline ${colorClass}`}>{capitalize(component.title)}</span>
              ),
            }
          }),
        ]
          .sort((a, b) => {
            return a.title < b.title ? -1 : 1
          })
          .map((x) => {
            return x.element
          })
        return (
          <>
            <ul className="list-disc list-inside" style={{ listStyleType: '"• "' }}>
              {listItems.map((element, idx) => {
                return <li key={idx}>{element}</li>
              })}
            </ul>
            <span className="inline px-2 py-px rounded cursor-pointer bg-backOpaque dark:bg-backOpaqueDark">
              {t('productName:less')}
            </span>
          </>
        )
      })()}
    </div>
  )
}

function NutrientsSection({ product }: { product: Product }) {
  const { t } = useAppContext()
  const { calories, proteins, fats, carbs } = product
  const text = [
    calories ? `${Math.ceil(calories)} ${t('productName:calories')}` : null,
    proteins ? `${Math.ceil(proteins)} ${t('productName:proteins')}` : null,
    fats ? `${Math.ceil(fats)} ${t('productName:fats')}` : null,
    carbs ? `${Math.ceil(carbs)} ${t('productName:carbohydrates')}` : null,
  ]
    .filter((v) => {
      return !!v
    })
    .join(', ')
  return <div className="pt-2 leading-tight text-opaque dark:text-opaqueDark">{text}</div>
}

type ModifiersSectionRef = {
  validate: () => boolean
}

const ModifiersSection = forwardRef(ModifiersSectionWithRef)

function ModifiersSectionWithRef(
  {
    product,
    chosenModifiers,
    onChosenModifiersChange,
  }: {
    product: Product
    chosenModifiers: NewOrderProductModifiers
    onChosenModifiersChange: (v: NewOrderProductModifiers, isValid: boolean) => void
  },
  ref: Ref<ModifiersSectionRef>,
) {
  const { t } = useAppContext()
  const [invalidGroups, setInvalidGroups] = useState<{ [groupName: string]: boolean }>({})
  const rootRef = useRef<HTMLDivElement>(null)
  const router = useRouter()

  useEffect(() => {
    const url = new URL(location.href)
    const shouldScrollToModifiers = url.searchParams.has(SCROLL_TO_MODIFIERS_QUERY_PARAM)
    if (shouldScrollToModifiers && rootRef.current) {
      url.searchParams.delete(SCROLL_TO_MODIFIERS_QUERY_PARAM)
      router.replace(
        '/venue/[venueName]/p/[productGroupName]/[productName]',
        url.pathname + url.search,
        { shallow: true },
      )
      scrollTo(rootRef.current)
    }
  }, [])

  const modifierGroupRefs = fromEntries(
    product.modifierGroups.map((group) => {
      // eslint-disable-next-line react-hooks/rules-of-hooks
      return [group.name, useRef<HTMLDivElement>(null)]
    }),
  )

  const validateGroup = (
    group: ProductModifierGroup,
    chosenModifiers: NewOrderProductModifiers,
  ) => {
    const chosenOptionsCount = keys(chosenModifiers[group.name] || {}).length
    const isRequiredButNotChosen =
      group.isRequired && group.options.length > 0 && chosenOptionsCount === 0
    const isExceedingMaxAllowedOptions = group.maxOptions && chosenOptionsCount > group.maxOptions

    const isValid = !isRequiredButNotChosen && !isExceedingMaxAllowedOptions

    setInvalidGroups((cur) => {
      return {
        ...cur,
        [group.name]: !isValid,
      }
    })

    return isValid
  }

  useImperativeHandle(ref, () => {
    return {
      validate() {
        const invalidGroups = product.modifierGroups
          .map((group) => {
            return { group, isValid: validateGroup(group, chosenModifiers) }
          })
          .filter(({ isValid }) => {
            return !isValid
          })

        const firstInvalidGroup = invalidGroups[0]
        if (firstInvalidGroup) {
          const element = modifierGroupRefs[firstInvalidGroup.group.name]?.current
          element && scrollTo(element)
        }

        return !firstInvalidGroup
      },
    }
  })

  const price = usePrice()

  const basketProductIdFromUrl = isBrowser
    ? new URL(location.href).searchParams.get(BASKET_PRODUCT_ID_QUERY_PARAM)
    : undefined

  return (
    <div ref={rootRef}>
      {product.modifierGroups
        .filter((mg) => {
          return !mg.options.every((option) => {
            return option.isInStopList
          })
        })
        .map((modifierGroup) => {
          return (
            <div
              ref={modifierGroupRefs[modifierGroup.name]}
              key={modifierGroup.name}
              style={
                invalidGroups[modifierGroup.name]
                  ? {
                      position: 'relative',
                      boxShadow: `0 0 3px 2px ${tailwind.theme.colors.negative}`,
                    }
                  : {}
              }
            >
              <div className="px-5 py-5 leading-tight bg-backOpaque dark:bg-backOpaqueDark">
                <div className="text-lg">{modifierGroup.title}</div>
                {(modifierGroup.isRequired || (modifierGroup.maxOptions || 0) > 1) && (
                  <div
                    className={`text-sm ${
                      invalidGroups[modifierGroup.name]
                        ? 'text-negative dark:text-negativeDark'
                        : 'text-opaque dark:text-opaqueDark'
                    }`}
                  >
                    {capitalize(
                      [
                        ...(modifierGroup.isRequired ? [t('common:required')] : []),
                        ...((modifierGroup.maxOptions || 0) > 1
                          ? [`${t('productName:noMore')} ${modifierGroup.maxOptions!}`]
                          : []),
                      ].join(', '),
                    )}
                  </div>
                )}
              </div>
              <div className="px-5">
                {modifierGroup.options
                  .filter((option) => {
                    return !option.isInStopList
                  })
                  .map((option, idx) => {
                    const isLastItem = idx === modifierGroup.options.length - 1
                    return (
                      <FilterableComponentOption
                        disabled={!!basketProductIdFromUrl}
                        selected={!!chosenModifiers[modifierGroup.name]?.[option.name]}
                        marker={modifierGroup.type === 'single' ? 'radio' : 'checkmark'}
                        title={option.title}
                        captionRight={
                          option.price > 0 ? `+${price.format(option.price)}` : undefined
                        }
                        key={option.name}
                        addSeparator={!isLastItem}
                        onSelected={(selected) => {
                          const newChosenModifiers = { ...chosenModifiers }

                          if (modifierGroup.type === 'single') {
                            // this mutation is very isolated and is performed on an explicit object's clone
                            // eslint-disable-next-line immutable/no-mutation
                            newChosenModifiers[modifierGroup.name] = { [option.name]: {} }
                          } else {
                            if (selected) {
                              // same as above
                              // eslint-disable-next-line immutable/no-mutation
                              newChosenModifiers[modifierGroup.name] = {
                                ...(newChosenModifiers[modifierGroup.name] || {}),
                                [option.name]: {},
                              }
                            } else {
                              // same as above
                              // eslint-disable-next-line immutable/no-mutation
                              delete newChosenModifiers[modifierGroup.name]?.[option.name]
                            }
                          }

                          const isValid = validateGroup(modifierGroup, newChosenModifiers)
                          onChosenModifiersChange(newChosenModifiers, isValid)
                        }}
                      />
                    )
                  })}
              </div>
            </div>
          )
        })}
    </div>
  )
}

export { getVenueStaticPaths as getStaticPaths }

export const getStaticProps: GetStaticProps<VenueBasedPageProps> = async (ctx) => {
  const orig = await getVenueStaticProps(ctx)
  return {
    ...orig,
    revalidate: 5,
  }
}
